import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { printPlugin } from '@react-pdf-viewer/print';
import { searchPlugin } from '@react-pdf-viewer/search';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { Box, CircularProgress, IconButton, Typography, Paper, Tooltip, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useSnackbar } from 'notistack';
import { getApiUrl } from '../../config/appConfig';
import api from '../../services/api';
import { compartirDocumentoPdf } from '../../services/documentoCompartidoService';
import { getApiErrorMessage } from '../../utils/apiError';

// Importar estilos
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/print/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';

interface PDFViewerProps {
  pdfUrl: string;
  onClose: () => void;
  title: string;
  documentId: string;
  documentType?:
    | 'recibo'
    | 'pedido'
    | 'cotizacion'
    | 'ajuste'
    | 'factura'
    | 'ordenPago'
    | 'nota-credito'
    | 'nota-debito'
    | 'remito'
    | 'transferenciaCaja'
    | 'cierreCaja'
    | 'cierreCajaIngresoEgreso'
    | 'cierreCajaPorTipoPago'
    | 'ordenCompra'
    | 'devolucion'
    /** PDF generado en el cliente (listados, etc.); sin URL pública de API */
    | 'informeCliente';
  downloadFileName?: string;
  onPrint?: () => void;
  onDownload?: () => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  pdfUrl,
  onClose,
  title,
  documentId,
  documentType = 'recibo',
  downloadFileName,
  onPrint,
  onDownload,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [sharingWhatsApp, setSharingWhatsApp] = useState(false);
  const overlayZ = theme.zIndex.modal + 2;
  const backdropZ = theme.zIndex.modal + 1;
  const printPluginInstance = printPlugin();
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const searchPluginInstance = searchPlugin();
  const zoomPluginInstance = zoomPlugin();

  useEffect(() => {
    document.documentElement.setAttribute('data-pdf-preview-open', '');
    return () => document.documentElement.removeAttribute('data-pdf-preview-open');
  }, []);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = downloadFileName || `${title}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    onDownload?.();
  };

  const handlePrint = () => {
    printPluginInstance.print();
    onPrint?.();
  };

  const handleShareWhatsApp = async () => {
    if (sharingWhatsApp) return;
    setSharingWhatsApp(true);
    try {
      const blob = await leerPdfParaCompartir(pdfUrl);
      const result = await compartirDocumentoPdf({
        blob,
        titulo: title,
        documentType,
        documentId,
        fileName: downloadFileName,
      });
      const message = encodeURIComponent(
        `Compartiendo ${title}\n\nPuedes descargar el documento aquí: ${result.url}\n\nEl enlace vence en 7 días.`
      );
      const phone = (result.telefono || '').replace(/\D/g, '');
      const whatsappUrl = phone
        ? `https://wa.me/${phone}?text=${message}`
        : `https://wa.me/?text=${message}`;
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo compartir el PDF por WhatsApp'), {
        variant: 'error',
      });
    } finally {
      setSharingWhatsApp(false);
    }
  };

  const viewer = (
    <>
      <Box
        className="pdf-viewer-backdrop"
        aria-hidden
        onClick={onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          zIndex: backdropZ,
          '@media print': { display: 'none' },
        }}
      />
      <Paper
        className="pdf-viewer-overlay"
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          height: '90%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 2,
          overflow: 'hidden',
          zIndex: overlayZ,
          '@media print': {
            position: 'static',
            transform: 'none',
            width: '100%',
            height: 'auto',
            maxHeight: 'none',
            boxShadow: 'none',
            p: 0,
            overflow: 'visible',
          },
        }}
      >
      <Box
        className="pdf-viewer-chrome"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          '@media print': { display: 'none' }
        }}
      >
        <Typography variant="h6">{title}</Typography>
        <Box>
          <Tooltip title="Compartir por WhatsApp">
            <span>
              <IconButton
                onClick={handleShareWhatsApp}
                color="success"
                disabled={sharingWhatsApp}
                aria-label="Compartir por WhatsApp"
              >
                {sharingWhatsApp ? <CircularProgress size={22} color="success" /> : <WhatsAppIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Imprimir (solo el PDF, sin cabecera de la aplicación)">
            <IconButton onClick={handlePrint} color="primary" aria-label="Imprimir PDF">
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Descargar PDF">
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cerrar">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          height: 'calc(100% - 64px)',
          overflow: 'hidden',
          '@media print': { height: 'auto', overflow: 'visible' }
        }}
      >
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <Viewer
            fileUrl={pdfUrl}
            plugins={[
              printPluginInstance,
              defaultLayoutPluginInstance,
              searchPluginInstance,
              zoomPluginInstance,
            ]}
            defaultScale={2}
            theme={{
              theme: 'light',
            }}
          />
        </Worker>
      </Box>
    </Paper>
    </>
  );

  return createPortal(viewer, document.body);
};

async function leerPdfParaCompartir(pdfUrl: string): Promise<Blob> {
  if (pdfUrl.startsWith('blob:') || pdfUrl.startsWith('data:')) {
    const response = await fetch(pdfUrl);
    if (!response.ok) {
      throw new Error('No se pudo leer el PDF para compartir.');
    }
    const blob = await response.blob();
    if (!blob || blob.size === 0) {
      throw new Error('No se pudo leer el PDF para compartir.');
    }
    return blob;
  }

  const apiBase = getApiUrl().replace(/\/$/, '');
  const path = pdfUrl.startsWith(apiBase) ? pdfUrl.slice(apiBase.length) : pdfUrl;
  const response = await api.get(path, { responseType: 'blob' });
  const blob = response.data as Blob;
  if (!blob || blob.size === 0) {
    throw new Error('No se pudo leer el PDF para compartir.');
  }
  return blob;
}

export default PDFViewer;
